import { Site } from '../types/interfaces';

export const FACILITY_VIEW_SITE_STORAGE_KEY = 'tilly.facilityView.siteId';

/** Internal bucket for keg asset locations — never show in Facility View / site rails. */
export const INTERNAL_SITE_IDS = new Set(['KEG-ASSETS']);

export type SiteKind = 'Brewery' | 'Distillery' | 'Taproom' | 'Other';

export function isInternalSite(siteId: string | null | undefined): boolean {
  return INTERNAL_SITE_IDS.has(String(siteId || '').toUpperCase());
}

/** Drop internal sites (defense in depth; API already excludes KEG-ASSETS). */
export function filterVisibleSites(sites: Site[]): Site[] {
  return (sites || []).filter((s) => !isInternalSite(s.siteId));
}

/** Normalize site.type into a short kind for badges. */
export function siteKind(site: Pick<Site, 'type' | 'siteId' | 'name'>): SiteKind {
  const t = String(site.type || '').trim().toLowerCase();
  if (t.includes('taproom')) return 'Taproom';
  if (t.includes('distill') || t.includes('dsp')) return 'Distillery';
  if (t.includes('brew')) return 'Brewery';
  // Fall back on license / name hints
  const id = String(site.siteId || '').toUpperCase();
  if (id.startsWith('DSP-')) return 'Distillery';
  if (id.startsWith('BR-')) return 'Brewery';
  if (id.startsWith('TAPROOM')) return 'Taproom';
  if (/taproom/i.test(site.name || '')) return 'Taproom';
  return 'Other';
}

export function readStoredFacilitySiteId(): string | null {
  try {
    const v = localStorage.getItem(FACILITY_VIEW_SITE_STORAGE_KEY);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function writeStoredFacilitySiteId(siteId: string): void {
  try {
    if (siteId) localStorage.setItem(FACILITY_VIEW_SITE_STORAGE_KEY, siteId);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Pick the active site: stored preference (if still present) → prop → first enabled.
 */
export function resolveFacilitySiteId(
  sites: Site[],
  preferredProp?: string | null,
  stored?: string | null
): string {
  const visible = filterVisibleSites(sites);
  const ids = new Set(visible.map((s) => s.siteId));
  if (stored && ids.has(stored)) return stored;
  if (preferredProp && ids.has(preferredProp)) return preferredProp;
  return visible[0]?.siteId || preferredProp || '';
}

/** Stable sort: breweries, distilleries, taprooms, then name. */
export function sortSitesForRail(sites: Site[]): Site[] {
  const order: Record<SiteKind, number> = {
    Brewery: 0,
    Distillery: 1,
    Taproom: 2,
    Other: 3,
  };
  return filterVisibleSites(sites).sort((a, b) => {
    const ka = order[siteKind(a)];
    const kb = order[siteKind(b)];
    if (ka !== kb) return ka - kb;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}
